import type { Product, ServerBoostProductConfig } from '#bv/data/products'
import { SERVER_BOOST_3M_PRODUCT_ID, SERVER_BOOST_PRODUCT_ID, SERVER_BOOST_PRODUCT_IDS } from '#bv/data/products'

export { SERVER_BOOST_3M_PRODUCT_ID, SERVER_BOOST_PRODUCT_ID, SERVER_BOOST_PRODUCT_IDS }

export type ServerBoostTier = { boosts: number; priceUsd: number }

/** Default 1-month volume tiers (see product #134). */
export const SERVER_BOOST_TIERS: ServerBoostTier[] = [
  { boosts: 2, priceUsd: 0.99 },
  { boosts: 8, priceUsd: 2.99 },
  { boosts: 14, priceUsd: 4.99 },
  { boosts: 20, priceUsd: 6.99 },
  { boosts: 30, priceUsd: 9.99 },
]

/** Default 3-month volume tiers (see product #135). */
export const SERVER_BOOST_3M_TIERS: ServerBoostTier[] = [
  { boosts: 2, priceUsd: 1.99 },
  { boosts: 8, priceUsd: 5.99 },
  { boosts: 14, priceUsd: 8.99 },
  { boosts: 20, priceUsd: 12.99 },
  { boosts: 30, priceUsd: 18.99 },
]

/** Defaults when config omits bounds (see `serverBoostProduct` in products.ts). */
export const SERVER_BOOST_MIN = 2
export const SERVER_BOOST_MAX = 30
/** Volume tier badge — unlocks Discord Level 3. */
export const SERVER_BOOST_BEST_VALUE_BOOSTS = 14

export function serverBoostBounds(cfg: ServerBoostProductConfig): { min: number; max: number } {
  return {
    min: cfg.minBoosts ?? SERVER_BOOST_MIN,
    max: cfg.maxBoosts ?? SERVER_BOOST_MAX,
  }
}

export type ServerBoostQuote = {
  boostCount: number
  usdPrice: number
  /** Tier used for volume rate (largest tier with boosts ≤ count). */
  appliedTier: ServerBoostTier
}

export function isServerBoostProductId(productId: number): boolean {
  return (SERVER_BOOST_PRODUCT_IDS as readonly number[]).includes(productId)
}

export function isServerBoostProduct(
  p: Product | null | undefined,
): p is Product & { serverBoostConfig: ServerBoostProductConfig } {
  return Boolean(p?.serverBoostConfig && isServerBoostProductId(p.id))
}

/** Shop “From” price — 2 boosts entry tier. */
export function serverBoostStorefrontFromPriceUsd(
  product: Product & { serverBoostConfig: ServerBoostProductConfig },
): number {
  if (product.price > 0) return product.price
  return quoteServerBoostPurchase(SERVER_BOOST_MIN, product.serverBoostConfig).usdPrice
}

export function clampServerBoostCount(raw: number, cfg: ServerBoostProductConfig): number {
  const n = Math.floor(raw)
  if (!Number.isFinite(n)) return cfg.minBoosts
  return Math.min(cfg.maxBoosts, Math.max(cfg.minBoosts, n))
}

function tiersFromConfig(cfg: ServerBoostProductConfig): ServerBoostTier[] {
  if (cfg.tiers?.length) return [...cfg.tiers].sort((a, b) => a.boosts - b.boosts)
  return SERVER_BOOST_TIERS
}

/** Fixed packages shown on the product page (no custom boost count). */
export function allowedServerBoostPresets(cfg: ServerBoostProductConfig): number[] {
  return tiersFromConfig(cfg).map((t) => t.boosts)
}

/** Volume rate: highest tier where tier.boosts ≤ count. */
export function appliedTierForBoostCount(count: number, cfg: ServerBoostProductConfig): ServerBoostTier {
  const tiers = tiersFromConfig(cfg)
  let applied = tiers[0]!
  for (const tier of tiers) {
    if (count >= tier.boosts) applied = tier
  }
  return applied
}

export function usdPriceForBoostCount(count: number, cfg: ServerBoostProductConfig): number {
  const n = clampServerBoostCount(count, cfg)
  const tier = appliedTierForBoostCount(n, cfg)
  const unit = tier.priceUsd / tier.boosts
  return Math.round(n * unit * 100) / 100
}

export function quoteServerBoostPurchase(boostCount: number, cfg: ServerBoostProductConfig): ServerBoostQuote {
  const boostCountClamped = clampServerBoostCount(boostCount, cfg)
  const appliedTier = appliedTierForBoostCount(boostCountClamped, cfg)
  return {
    boostCount: boostCountClamped,
    usdPrice: usdPriceForBoostCount(boostCountClamped, cfg),
    appliedTier,
  }
}

export function serverBoostCartLineName(
  quote: ServerBoostQuote,
  cfg?: ServerBoostProductConfig,
): string {
  const label = cfg?.durationLabel?.trim()
  return label
    ? `Discord Server Boost — ${quote.boostCount}× (${label})`
    : `Discord Server Boost — ${quote.boostCount}×`
}

/** Accepts discord.gg invites, discord.com/invite/…, or discord.com/channels/GUILD_ID/… */
export function normalizeDiscordServerLink(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  try {
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    const url = new URL(withProto)
    const host = url.hostname.toLowerCase().replace(/^www\./, '')
    if (host === 'discord.gg') {
      const code = url.pathname.replace(/^\//, '').split('/').filter(Boolean)[0]
      if (!code || code.length < 2) return null
      return `https://discord.gg/${code}`
    }
    if (host === 'discord.com') {
      const inviteMatch = url.pathname.match(/^\/invite\/([^/]+)/i)
      if (inviteMatch?.[1]) return `https://discord.gg/${inviteMatch[1]}`
      const guildMatch = url.pathname.match(/^\/channels\/(\d{17,20})(?:\/|$)/i)
      if (guildMatch?.[1]) return `https://discord.com/channels/${guildMatch[1]}`
    }
    return null
  } catch {
    return null
  }
}

/** @deprecated Use normalizeDiscordServerLink */
export const normalizeDiscordInviteLink = normalizeDiscordServerLink

export function validateDiscordServerLinkMessage(raw: string | undefined): string | null {
  if (!raw?.trim()) return 'Paste your Discord server link (required).'
  if (!normalizeDiscordServerLink(raw)) {
    return 'Enter a valid Discord server link: discord.gg/…, discord.com/invite/…, or discord.com/channels/…'
  }
  return null
}

/** @deprecated Use validateDiscordServerLinkMessage */
export const validateDiscordInviteLinkMessage = validateDiscordServerLinkMessage

export function serverBoostMetadataForCheckout(
  quote: ServerBoostQuote,
  inviteLink: string,
  cfg?: ServerBoostProductConfig,
): Record<string, string> {
  const link = normalizeDiscordServerLink(inviteLink)
  if (!link) throw new Error('Invalid Discord server link.')
  return {
    serverBoost: '1',
    serverBoostCount: String(quote.boostCount),
    serverBoostUsd: quote.usdPrice.toFixed(2),
    serverBoostTierBoosts: String(quote.appliedTier.boosts),
    serverBoostInviteLink: link,
    ...(cfg?.durationMonths != null ? { serverBoostDurationMonths: String(cfg.durationMonths) } : {}),
    ...(cfg?.durationLabel ? { serverBoostDurationLabel: cfg.durationLabel } : {}),
  }
}

export function validateServerBoostCartLine(
  line: {
    productId: number
    unitPrice: number
    quantity: number
    serverBoostCount?: number
    serverBoostInviteLink?: string
  },
  product: Product | null,
): string | null {
  if (!isServerBoostProduct(product)) return 'Server Boost product is not available.'
  if (line.quantity !== 1) return 'Server Boost orders must be quantity 1.'
  const linkErr = validateDiscordServerLinkMessage(line.serverBoostInviteLink)
  if (linkErr) return linkErr
  const count = line.serverBoostCount
  if (count == null) return 'Select a boost package on the product page.'
  const presets = allowedServerBoostPresets(product.serverBoostConfig)
  const n = Math.floor(count)
  if (!presets.includes(n)) return 'Invalid boost package. Choose a listed tier on the product page.'
  const quote = quoteServerBoostPurchase(n, product.serverBoostConfig)
  if (quote.boostCount !== n) return 'Invalid boost package.'
  if (Math.abs(quote.usdPrice - line.unitPrice) > 0.02) {
    return 'Server Boost price mismatch. Refresh the product page and try again.'
  }
  return null
}
